import type { DeploymentStage, ExecutionStep } from '../../api';

/**
 * Where a step sits (CO-150). The run numbers its steps globally (step 4
 * of 27); the page shows them inside their stages (stage 2, step 3 of 6).
 * One place turns a step into both, so a summary never says "Step 4" for
 * the item the list shows as the third of its stage.
 */
export interface StepPlace {
  key: string;
  title: string;
  /** 1-based position of the stage, and how many stages there are. */
  stageNumber: number;
  stageCount: number;
  stageTitle: string;
  /** 1-based position inside the stage, and the stage's step count. */
  stepNumber: number;
  stepCount: number;
  /** The run's own sequence ("step 4 of 27 in the run"). */
  runSequence?: number;
  runSteps: number;
}

export function placeOfStep(key: string, stages: DeploymentStage[], steps: ExecutionStep[]): StepPlace | null {
  const step = steps.find((candidate) => candidate.step_key === key);
  const stageIndex = stages.findIndex((stage) => stage.step_keys.includes(key));
  if (stageIndex < 0) {
    if (!step) return null;
    const index = steps.indexOf(step);
    return { key, title: step.title, stageNumber: 0, stageCount: 0, stageTitle: '', stepNumber: index + 1, stepCount: steps.length, runSequence: step.sequence, runSteps: steps.length };
  }
  const stage = stages[stageIndex]!;
  // A stage lists the keys the run has; the step list shows those it knows.
  const known = stage.step_keys.filter((stepKey) => steps.some((candidate) => candidate.step_key === stepKey));
  const keys = known.length > 0 ? known : stage.step_keys;
  return {
    key,
    title: step?.title ?? key,
    stageNumber: stageIndex + 1,
    stageCount: stages.length,
    stageTitle: stage.title,
    stepNumber: keys.indexOf(key) + 1,
    stepCount: keys.length,
    runSequence: step?.sequence,
    runSteps: steps.length,
  };
}

/** "Stage 2 of 9 (Prepare the release), step 3 of 6". */
export function placeWords(place: StepPlace): string {
  if (place.stageNumber === 0) return `Step ${place.stepNumber} of ${place.stepCount}`;
  return `Stage ${place.stageNumber} of ${place.stageCount} (${place.stageTitle}), step ${place.stepNumber} of ${place.stepCount}`;
}

/** The running stage, and how many are done, for a deployment in progress. */
export function progressOf(stages: DeploymentStage[]) {
  const done = stages.filter((stage) => stage.status === 'succeeded' || stage.status === 'warning' || stage.status === 'skipped').length;
  const current = stages.find((stage) => stage.status === 'running') ?? null;
  const switchStage = stages.find((stage) => stage.key === 'switch');
  const traffic: 'switched' | 'switching' | 'not_yet' | 'unknown' = !switchStage
    ? 'unknown'
    : switchStage.status === 'succeeded' || switchStage.status === 'warning'
      ? 'switched'
      : switchStage.status === 'running'
        ? 'switching'
        : 'not_yet';
  return { done, total: stages.length, current, currentNumber: current ? stages.indexOf(current) + 1 : null, traffic };
}
